import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Confirmation } from '@Components/index';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import {
  getModalRecordKey,
  getModalStatus,
  getModalType,
} from '@Store/selectors/modalSelector';
import { MSkeleton } from '@Components/Base';
import { getDocument } from '@Features/utils/document';
import { modalActions } from '@Store/slices/modalSlice';
import { NotificationContext } from '@Context/NotificationContext';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { getDocumentTypesDict } from '@Store/selectors/dictionarySelector';
import MRFeatureRoutePath from '@Features/router/mr/MRFeatureRoutePath.enum';
import {
  MaterialRequisitionDocumentRequest,
  MaterialRequisitionItemFormResponse,
} from '@Types/api/form';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import MaterialConfirmationActions from './components/MaterialConfirmationActions';

import ContentConfirmationResolver from './components/ContentConfirmationResolver/ContentConfirmationResolver';
import { ViewTab } from '../ViewTab.enum';
import {
  useGetRequisitionDocumentsMutation,
  useGetRequisitionItemsMutation,
  useDeleteRequisitionItemFormMutation,
  useSubmitRequestMutation,
  usePostRequisitionItemFormMutation,
  usePostRequisitionDocumentMutation,
  useDeleteRequisitionDocumentMutation,
  useGetRequisitionCodeDescItemsMutation,
} from '../../store/api/MaterialRequestApiSlice';

import { currentMaterialRequest } from '../../store/selectors/materialRequestSelector';
import { ResponseListType } from './types';
import useColumnSettings from './Columns/useColumnSettings';
import { MapColumns } from './Columns/MapColumns';
import useRenderActionColumn from './Columns/useRenderActionColumn';

import { dataSourceMap } from './DataSource/DataSource';
import { ItemEditModal, ItemViewModal, ItemDeleteModal } from './components';
import { materialRequestActions } from '../../store/materialRequestSlice';
import { getDocumentFolder } from '../utils/document';

const backText = 'Back to my requests';

const captionText = 'Material Confirmation';

const MaterialConfirmScreen: React.FC = () => {
  const { errorNotification, submitSuccessNotification } =
    useContext(NotificationContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentMRequest = useAppSelector(currentMaterialRequest);
  const isModalOpen = useAppSelector(getModalStatus);
  const modalItemId = useAppSelector(getModalRecordKey);
  const typeModal = useAppSelector(getModalType);
  const [activeTab, setActiveTab] = useState<keyof typeof ViewTab>(
    ViewTab.DETAILS
  );
  const [getRequisitionCodeDescItems] =
    useGetRequisitionCodeDescItemsMutation();
  const [getRequisitionItems, { isLoading }] = useGetRequisitionItemsMutation();
  const [getRequisitionDocuments] = useGetRequisitionDocumentsMutation();
  const [saveRequisitionMaterialItem] = usePostRequisitionItemFormMutation();
  const [saveRequisitionDocumentItem] = usePostRequisitionDocumentMutation();
  const [deleteRequisitionItem] = useDeleteRequisitionItemFormMutation();
  const [deleteItemDocument] = useDeleteRequisitionDocumentMutation();
  const [submitRequest, submitRequestState] = useSubmitRequestMutation();
  const [getFormList] = useFormListMutation();
  const [requisitionItems, setRequisitionItems] =
    useState<RequestListSearchResponse<MaterialRequisitionItemFormResponse>>();
  const [dataList, setDataList] = useState<ResponseListType>();
  const columnSettings = useColumnSettings({ activeTab, dataSource: [] });
  const renderColumns = useRenderActionColumn({
    mapColumns: MapColumns[activeTab],
  });
  const documentTypes = useAppSelector(getDocumentTypesDict);

  const [documentId, setDocumentId] = useState('');
  const [isFetched, setIsFetched] = useState(false);
  const { data: fileUrl } = useDownloadDocumentQuery(documentId || '', {
    skip: !documentId,
  });

  const refetchRequisitionItems = useCallback(() => {
    setIsFetched(true);
    getRequisitionItems({
      requisitionId: currentMRequest.requestId,
    })
      .unwrap()
      .then((response) => {
        setRequisitionItems(response);
        setIsFetched(false);
      });
  }, [currentMRequest.requestId]);

  const refetchRequisitionDocuments = useCallback(() => {
    setIsFetched(true);
    getRequisitionDocuments(String(currentMRequest.requestId))
      .unwrap()
      .then((response) => {
        setDataList(response);
        setIsFetched(false);
      });
  }, [currentMRequest.requestId]);

  useEffect(() => {
    if (activeTab === ViewTab.MATERIALS) {
      columnSettings.clearState();
      setIsFetched(true);
      getRequisitionItems({
        requisitionId: currentMRequest.requestId,
      })
        .unwrap()
        .then((response) => {
          setRequisitionItems(response);
          setIsFetched(false);
        });
    }
  }, [activeTab, currentMRequest.requestId]);

  useEffect(() => {
    if (requisitionItems?.content?.length && !isLoading) {
      getRequisitionCodeDescItems({ reqId: Number(currentMRequest.requestId) })
        .unwrap()
        .then((descriptionData) => {
          const mappedData = requisitionItems?.content?.map(
            (requisitionItem) => {
              const findDescriptionItem = descriptionData.find(
                (itemDesc) => itemDesc.reqItemId === requisitionItem.reqItemId
              );

              return findDescriptionItem
                ? {
                    ...requisitionItem,
                    actName: findDescriptionItem.data.actName,
                    resDesc: findDescriptionItem.data.resDesc,
                    matDesc: findDescriptionItem.data.matDesc,
                    plantCode: findDescriptionItem.data.plantCode,
                    plantName: findDescriptionItem.data.plantName,
                    ledgerName: findDescriptionItem.data.ledgerName,
                  }
                : requisitionItem;
            }
          );

          setDataList(mappedData);
        });
    }
  }, [requisitionItems, isLoading]);

  useEffect(() => {
    if (activeTab === ViewTab.DOCUMENTS) {
      columnSettings.clearState();
      setIsFetched(true);
      getRequisitionDocuments(String(currentMRequest.requestId))
        .unwrap()
        .then((response) => {
          setDataList(response);
          setIsFetched(false);
        });
    }
  }, [activeTab, currentMRequest.requestId]);

  const onDownloadDocument = useCallback((): void => {
    const filename = modalItemId;
    if (fileUrl && filename) {
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      dispatch(
        modalActions.setStatusModal({
          key: '',
          status: false,
          type: '',
        })
      );
    }
  }, [fileUrl, modalItemId]);

  useEffect(() => {
    if (typeModal !== 'Download') {
      return;
    }

    const documentFolderPath = getDocumentFolder(currentMRequest?.requestId);
    const documentFileName = modalItemId;

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, documentFileName);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [currentMRequest, typeModal, modalItemId]);

  useEffect(() => {
    if (fileUrl) {
      onDownloadDocument();
    }
  }, [fileUrl]);

  const dataSource = useMemo(
    () => dataSourceMap(dataList!, documentTypes),
    [dataList, documentTypes]
  );

  const handleDeleteRequisitionItem = useCallback(
    (deleteItems?: Array<number>) => {
      const deleteItem = Array.isArray(deleteItems)
        ? deleteItems
        : [modalItemId];

      deleteRequisitionItem(deleteItem)
        .unwrap()
        .then(() => {
          dispatch(
            modalActions.setStatusModal({
              key: '',
              status: false,
              type: '',
            })
          );
          refetchRequisitionItems();
        });
    },
    [modalItemId]
  );

  const handleDeleteItemDocument = useCallback(
    (deleteItems?: Array<MaterialRequisitionDocumentRequest>) => {
      const deleteItem = Array.isArray(deleteItems)
        ? deleteItems
        : [JSON.parse(modalItemId)];

      deleteItemDocument({
        body: deleteItem,
        requisitionId: currentMRequest.requestId,
      })
        .unwrap()
        .then(() => {
          dispatch(
            modalActions.setStatusModal({
              key: '',
              status: false,
              type: '',
            })
          );
          refetchRequisitionDocuments();
        });
    },
    [currentMRequest.requestId, modalItemId]
  );

  const handleSaveMaterialItem = useCallback(
    (formikValues: MaterialRequisitionItemFormResponse) => {
      if (formikValues) {
        saveRequisitionMaterialItem(formikValues)
          .unwrap()
          .then(() => {
            dispatch(
              modalActions.setStatusModal({
                key: '',
                status: false,
                type: '',
              })
            );
            refetchRequisitionItems();
          });
      }
    },
    []
  );

  const handleSaveDocumentItem = useCallback(
    (formikValues: MaterialRequisitionDocumentRequest) => {
      if (formikValues) {
        saveRequisitionDocumentItem(formikValues)
          .unwrap()
          .then(() => {
            dispatch(
              modalActions.setStatusModal({
                key: '',
                status: false,
                type: '',
              })
            );
            refetchRequisitionDocuments();
          });
      }
    },
    []
  );

  const handleCloseModal = (): void => {
    dispatch(
      modalActions.setStatusModal({
        key: '',
        status: false,
        type: '',
      })
    );
  };

  const handleBackToRequests = (): void => {
    navigate(
      `/mr/${MRFeatureRoutePath.MANAGE_REQUESTS}/${MRFeatureRoutePath.MANAGE_REQUEST_MATERIAL_REQUEST}`,
      { state: { redirected: true } }
    );
    dispatch(eventMessageActions.sendEventType('CONFIRM_REFETCH'));
    dispatch(materialRequestActions.clearForm());
  };

  const handleConfirmRequest = useCallback((): void => {
    if (submitRequestState.isLoading) return;

    submitRequest(String(currentMRequest?.requestId))
      .unwrap()
      .then(() => {
        submitSuccessNotification(String(currentMRequest?.requestId));
        dispatch(materialRequestActions.clearForm());
      })
      .catch(({ data }) => {
        errorNotification(data);
      });

    handleBackToRequests();
  }, [currentMRequest?.requestId, submitRequestState.isLoading]);

  return (
    <Confirmation
      handleBack={handleBackToRequests}
      handleCancel={handleBackToRequests}
      handleConfirm={handleConfirmRequest}
      backToText={backText}
      caption={captionText}
      actions={
        <MaterialConfirmationActions
          isFetched={isFetched}
          setActiveTab={setActiveTab}
        />
      }
      content={
        <>
          {!isFetched ? (
            <ContentConfirmationResolver
              columns={renderColumns}
              columnSettings={columnSettings}
              dataSource={dataSource}
              activeTab={activeTab}
              dataList={dataList}
              onDeleteItems={
                activeTab === 'MATERIALS'
                  ? handleDeleteRequisitionItem
                  : handleDeleteItemDocument
              }
            />
          ) : (
            <div>
              <MSkeleton repeat={1} className="skeleton-item" />
            </div>
          )}
          {typeModal === 'View' && (
            <ItemViewModal showModal={isModalOpen} activeTab={activeTab} />
          )}
          {typeModal === 'Edit' && (
            <ItemEditModal
              itemId={modalItemId}
              showModal={isModalOpen}
              activeTab={activeTab}
              onSaveMaterialItem={handleSaveMaterialItem}
              onSaveDocumentItem={handleSaveDocumentItem}
              onClose={handleCloseModal}
            />
          )}
          {typeModal === 'Delete' && (
            <ItemDeleteModal
              handleDeleteItemMaterial={handleDeleteRequisitionItem}
              handleDeleteItemDocument={handleDeleteItemDocument}
              deleteName={modalItemId}
              showModal={isModalOpen}
              activeTab={activeTab}
            />
          )}
        </>
      }
      hidePagination={activeTab === ViewTab.DETAILS}
      paginationSettings={{
        current: 1,
        pageSize: dataSource.length,
        total: dataSource.length,
        onChange: () => null,
      }}
    />
  );
};

export default MaterialConfirmScreen;
